import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query';
import type { CursorPaginatedResponse, CursorParams } from '../types/api.types';

const DEFAULT_PAGE_SIZE = 10;

interface UseInfiniteScrollOptions<T> {
	queryKey: QueryKey;
	queryFn: (params: CursorParams) => Promise<CursorPaginatedResponse<T>>;
	pageSize?: number;
	search?: string;
	enabled?: boolean;
}

interface UseInfiniteScrollReturn<T> {
	/** Flattened items from all loaded pages */
	items: T[];
	/** True during initial load */
	isLoading: boolean;
	/** True while fetching the next page */
	isFetchingNextPage: boolean;
	/** Whether more pages are available */
	hasNextPage: boolean;
	/** Call this to load the next page */
	fetchNextPage: () => void;
}

/**
 * Hook for infinite scroll with cursor-based pagination.
 *
 * Wraps useInfiniteQuery and exposes fetchNextPage for the DataTable
 * scroll handler to call when the user scrolls near the bottom.
 */
export function useInfiniteScroll<T>({
	queryKey,
	queryFn,
	pageSize = DEFAULT_PAGE_SIZE,
	search,
	enabled = true,
}: UseInfiniteScrollOptions<T>): UseInfiniteScrollReturn<T> {
	const {
		data,
		isLoading,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
	} = useInfiniteQuery({
		queryKey,
		queryFn: ({ pageParam }) =>
			queryFn({ cursor: pageParam as string | undefined, pageSize, search }),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage) =>
			lastPage.hasNext ? lastPage.nextCursor : undefined,
		enabled,
	});

	const items = data?.pages.flatMap((page) => page.items) ?? [];

	return {
		items,
		isLoading,
		isFetchingNextPage,
		hasNextPage: hasNextPage ?? false,
		fetchNextPage,
	};
}
